import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar, Badge, Button, Card, Center, Group, Loader, ScrollArea, Drawer,
  SimpleGrid, Stack, Text, ThemeIcon, ActionIcon, Tooltip, Divider, Select,
} from '@mantine/core';
import {
  IconSearch, IconUserPlus, IconUsers, IconPhone, IconMail, IconTrash, IconFileImport, IconWand,
  IconRefresh,
} from '@tabler/icons-react';
import { TextInput, MemberDetailModal } from '@/components';
import {
  useRegistrationService, useMemberService, useMemberTypeService, useRegistrationIntakeService,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';

import ImportFromIntakeModal from './ImportFromIntakeModal';
import MergeFromSubmissionModal from '@/components/common/MergeFromSubmissionModal';

interface MembersTabProps {
  registrationId: number;
  active: boolean;
  onRegistrationChange?: () => void;
  /**
   * The intake this registration was created from, when the record stores it.
   *
   * ⚠️ Null on registrations predating `Registration.intakeID` (SDK 0.0.52) — which is most of the
   * existing ones. The import falls back to the intake list's `registrationID` filter in that case, so
   * the action is always offered and reports honestly when there is genuinely no intake behind it.
   */
  intakeID?: number | null;
}

// Shape from searchMembers response data[]
interface Member {
  memberID: number;
  userId: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  maritalStatusID: number;
  email: string;
  cellNumber: string | null;
  altContact: string | null;
  telephone: string | null;
  isMember: boolean;
  isActive: boolean;
  avatar: string | null;
  typeID: number;
  memberCode: string | null;
  modifiedDateTime: number;
  createdDateTime: number | null;
  // joined locally after enrichment
  isMain?: boolean;
  /** Registered the enrolment, as opposed to someone who is only enrolled (SDK 0.0.58). */
  isApplicant?: boolean;
  hasConsent?: boolean;
}

interface MemberType {
  typeID: number;
  name: string;
}

const fullName = (m: Member) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || `Member ${m.memberID}`;

const getInitials = (m: Member) =>
  [m.firstName?.[0], m.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'M';

// ── Member grid card ──────────────────────────────────────────────────────────
function MemberCard({
  member,
  onRemove,
  removing,
  onOpenDetails,
  onFillFromIntake,
}: {
  member: Member;
  onRemove: () => void;
  removing: boolean;
  onOpenDetails: () => void;
  /** Present only when the intake captured something this member record is missing. */
  onFillFromIntake?: () => void;
}) {
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      h="100%"
      onClick={onOpenDetails}
      style={{ cursor: 'pointer' }}
    >
      {/* Avatar centered */}
      <Group justify="center" mb="sm">
        <Avatar src={member.avatar ?? undefined} alt={fullName(member)} size="xl" radius="md">
          {getInitials(member)}
        </Avatar>
      </Group>

      {/* Name */}
      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {fullName(member)}
      </Text>

      {/* Main participant / consent badges */}
      <Group justify="center" gap={6} mb={4}>
        {/* ⚠️ Independent flags: a self-registration is both applicant and enrolled; someone who
            registered another person is the applicant but not a participant. */}
        {member.isApplicant && (
          <Badge size="sm" variant="light" color="grape">Applicant</Badge>
        )}
        {member.isMain && (
          <Badge size="sm" variant="light" color="blue">Main Participant</Badge>
        )}
        <Badge size="sm" variant="light" color={member.hasConsent ? 'green' : 'red'}>
          {member.hasConsent ? 'Consent Given' : 'No Consent'}
        </Badge>
      </Group>

      {/* Contact details */}
      <Stack align="center" gap={2} mt="xs">
        {member.email
          ? <Text size="sm" c="dimmed">Email: {member.email}</Text>
          : <Text size="sm" c="dimmed">No email on record</Text>
        }
        {(member.cellNumber || member.telephone || member.altContact)
          ? <Text size="sm" c="dimmed">Phone: {member.cellNumber ?? member.telephone ?? member.altContact}</Text>
          : <Text size="sm" c="dimmed">No phone on record</Text>
        }
      </Stack>

      <Group justify="flex-end" gap={6} mt="md">
        {/* Only rendered when there is genuinely something to apply — see `submittedFor`. */}
        {onFillFromIntake && (
          <Tooltip label="Fill missing details from the sign-up" withArrow>
            <ActionIcon
              variant="light"
              color="teal"
              onClick={(e) => { e.stopPropagation(); onFillFromIntake(); }}
            >
              <IconWand size={16} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Remove from registration" withArrow>
          <ActionIcon
            variant="light"
            color="red"
            loading={removing}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
}

// ── Search result card ────────────────────────────────────────────────────────
function SearchResultCard({
  member,
  alreadyAdded,
  addingId,
  onAdd,
}: {
  member: Member;
  alreadyAdded: boolean;
  addingId: number | null;
  onAdd: (isMain: boolean) => void;
}) {
  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="md" wrap="nowrap" style={{ overflow: 'hidden', flex: 1 }}>
          <Avatar src={member.avatar ?? undefined} radius="xl" size={44} color="teal">
            {getInitials(member)}
          </Avatar>
          <Stack gap={2} style={{ overflow: 'hidden', flex: 1 }}>
            <Text fw={600} size="sm" truncate>{fullName(member)}</Text>
            {member.email && (
              <Group gap={6} wrap="nowrap">
                <IconMail size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                <Text size="xs" c="dimmed" truncate>{member.email}</Text>
              </Group>
            )}
            {(member.cellNumber || member.telephone) && (
              <Group gap={6} wrap="nowrap">
                <IconPhone size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                <Text size="xs" c="dimmed">{member.cellNumber ?? member.telephone}</Text>
              </Group>
            )}
          </Stack>
        </Group>

        {alreadyAdded ? (
          <Badge color="gray" variant="light" style={{ flexShrink: 0 }}>Already added</Badge>
        ) : (
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Button
              size="xs"
              variant="light"
              loading={addingId === member.memberID}
              leftSection={<IconUserPlus size={14} />}
              onClick={() => onAdd(false)}
            >
              Add
            </Button>
            <Button
              size="xs"
              loading={addingId === member.memberID}
              onClick={() => onAdd(true)}
            >
              Add as Main
            </Button>
          </Group>
        )}
      </Group>
    </Card>
  );
}

function MemberCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <Avatar radius="xl" size={52} color="gray">M</Avatar>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Loading…</Text>
          <Text size="xs" c="dimmed">—</Text>
          <Text size="xs" c="dimmed">—</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Add Members Drawer ──────────────────────────────────────────────────
function AddMembersDrawer({
  opened,
  onClose,
  registrationId,
  alreadyAdded,
  onAdded,
}: {
  opened: boolean;
  onClose: () => void;
  registrationId: number;
  alreadyAdded: (m: Member) => boolean;
  onAdded: () => Promise<void> | void;
}) {
  const registrationService = useRegistrationService();
  const memberService = useMemberService();
  const memberTypeService = useMemberTypeService();

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Member[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [allMemberTypes, setAllMemberTypes] = useState<MemberType[]>([]);
  const [creatingMember, setCreatingMember] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [cellNumber, setCellNumber] = useState('');
  const [typeID, setTypeID] = useState<string | null>(null);

  // Fetch member types once drawer expands
  useEffect(() => {
    if (!opened) return;
    memberTypeService.getAll()
      .then((res: any) => setAllMemberTypes(res?.data ?? res ?? []))
      .catch(console.error);
  }, [opened, memberTypeService]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res: any = await memberService.searchMembers(search.trim());
        const list: Member[] = res?.data ?? res ?? [];
        setResults(list);
      } catch (e) {
        console.error('Error searching members:', e);
        toast.error('Error', 'Could not search members.');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, memberService]);

  const resetCreateForm = () => {
    setFirstName(''); setLastName(''); setEmail(''); setCellNumber(''); setTypeID(null);
    setShowCreate(false);
  };

  const resetAll = () => {
    setSearch(''); setResults([]);
    resetCreateForm();
  };

  const addToRegistration = async (member: Member, isMain: boolean) => {
    setAddingId(member.memberID);
    try {
      await registrationService.addMember(registrationId, member.memberID, isMain);
      await onAdded();
      toast.success('Member Added', `${fullName(member)} has been added to the registration.`);
      setSearch('');
      setResults([]);
    } catch (e) {
      console.error('Error adding member:', e);
      toast.error('Error', 'Could not add member. Please try again.');
    } finally {
      setAddingId(null);
    }
  };

  // Create a brand-new member and immediately bind them to registration
  const createAndAddMember = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !typeID) return;
    setCreatingMember(true);
    try {
      const newMember: Partial<Member> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        cellNumber: cellNumber.trim() || null,
        typeID: Number(typeID),
        isMember: false,
        isActive: true,

      };

      const res: any = await memberService.create(newMember as Member);
      const created: Member = res?.data ?? res;

      await registrationService.addMember(registrationId, created.memberID, false);
      await onAdded();

      toast.success('Member Created', `${fullName(created)} was created and added to the registration.`);
      resetCreateForm();
      setSearch('');
      setResults([]);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to create member.');
    } finally {
      setCreatingMember(false);
    }
  };

  const noResults = !searching && search.trim().length > 0 && results.length === 0;

  return (
    <Drawer
      opened={opened}
      onClose={() => { onClose(); resetAll(); }}
      title={
        <Group gap="xs">
          <IconUserPlus size={18} />
          <Text fw={600}>Add Registration Members</Text>
        </Group>
      }
      position="right"
      size={620}
      padding="md"
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search members by name…"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {searching && <Center py="sm"><Loader size="sm" /></Center>}

        {!searching && results.length > 0 && (
          <ScrollArea.Autosize mah={390}>
            <Stack gap="xs">
              {results.map((m) => (
                <SearchResultCard
                  key={m.memberID}
                  member={m}
                  alreadyAdded={alreadyAdded(m)}
                  addingId={addingId}
                  onAdd={(isMain) => addToRegistration(m, isMain)}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}

        {noResults && !showCreate && (
          <Card withBorder radius="md" padding="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">No members found for "{search}".</Text>
              <Button size="xs" variant="light" onClick={() => setShowCreate(true)}>
                Create New Member
              </Button>
            </Group>
          </Card>
        )}

        {showCreate && (
          <Card withBorder radius="md" padding="sm">
            <Stack gap="sm">
              <Text size="sm" fw={600}>New Member</Text>
              <Group grow>
                <TextInput
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.currentTarget.value)}
                />
                <TextInput
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.currentTarget.value)}
                />
              </Group>
              <TextInput
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              <TextInput
                placeholder="Cell number"
                value={cellNumber}
                onChange={(e) => setCellNumber(e.currentTarget.value)}
              />
              <Select
                placeholder="Member type"
                data={allMemberTypes.map((t) => ({ value: String(t.typeID), label: t.name }))}
                value={typeID}
                onChange={setTypeID}
                searchable
                clearable
              />
              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" size="xs" onClick={resetCreateForm}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  loading={creatingMember}
                  disabled={!firstName.trim() || !lastName.trim() || !email.trim() || !typeID}
                  onClick={createAndAddMember}
                >
                  Create & Add
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {!search.trim() && (
          <>
            <Divider />
            <Text size="sm" c="dimmed" ta="center">
              Search for a member above, or create a new one if they don't exist yet.
            </Text>
          </>
        )}
      </Stack>
    </Drawer>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MembersTab({
  registrationId, active, onRegistrationChange, intakeID,
}: MembersTabProps) {
  const registrationService = useRegistrationService();

  /**
   * Member ids already on the registration.
   *
   * ⚠️ Just the ids — this is only ever used to grey out people already added. Holding the full
   * `MemberRegistration` here meant inventing `createdDateTime`/`modifiedDateTime` values that nothing
   * reads, which is what forced a cast.
   */
  const memberService = useMemberService();
  const intakeService = useRegistrationIntakeService();
  const [regMemberIDs, setRegMemberIDs] = useState<number[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  /** Card click opens the read-only detail modal. */
  const [detailMember, setDetailMember] = useState<Member | null>(null);

  // Drawer controlling state
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  /** The member whose sign-up details are being reviewed, or null. */
  const [mergeMember, setMergeMember] = useState<Member | null>(null);
  /**
   * What the intake captured, keyed by memberID.
   *
   * ⚠️ Loaded once per registration, not per member. The registration's own participant rows read
   * from the **member cache**, so they cannot answer "what did this person actually type?" — only the
   * intake can. Empty when the registration has no intake behind it, which disables the action.
   */
  const [submittedByMemberID, setSubmittedByMemberID] = useState<Record<number, {
    dateOfBirth?: string | null; gender?: string | null; email?: string | null; phone?: string | null;
  }>>({});

  /**
   * Membership comes from `getMembers()`; the display details come from `getView().participants`.
   *
   * ⚠️ **These are two different sources and they can disagree.** `getMembers()` returns the
   * `MemberRegistration` junction rows — the actual "who is on this registration" list, and what
   * `importFromIntake` writes. `getView().participants` is built from the staged participant *details*
   * (note its `detailID`), so a member linked without a matching detail row does **not** appear there.
   *
   * An earlier version of this read `participants` alone. That looked cleaner and removed an N+1, but
   * it meant a successful import reported "imported" while the list stayed unchanged — the member was
   * linked, just not present in the view's participant list. Membership must win.
   *
   * The enrichment is still one call in the normal case: only members with no matching participant row
   * fall back to a per-member lookup, which is usually none.
   */
  const fetchMembers = useCallback(async () => {
    if (!registrationId) return;
    setLoadingMembers(true);
    try {
      const [rawLinks, view] = await Promise.all([
        registrationService.getMembers(registrationId),
        registrationService.getView(registrationId).catch(() => null),
      ]);

      const links = ((rawLinks as unknown as { data?: unknown })?.data ?? rawLinks) as
        { memberID: number; isMain: boolean; isApplicant: boolean; hasConsent: boolean }[];
      const linkRows = Array.isArray(links) ? links : [];
      setRegMemberIDs(linkRows.map((l) => l.memberID).filter((id) => id != null));

      const byMemberID = new Map(
        (view?.participants ?? [])
          .filter((p) => p.memberID != null)
          .map((p) => [p.memberID as number, p]),
      );

      const enriched = await Promise.all(linkRows.map(async (link) => {
        const p = byMemberID.get(link.memberID);
        if (p) {
          return {
            memberID: link.memberID,
            userId: '',
            firstName: (p.name ?? '').trim() || 'Member',
            middleName: null,
            lastName: (p.surname ?? '').trim(),
            dateOfBirth: p.dateOfBirth ?? null,
            gender: p.gender ?? null,
            maritalStatusID: 0,
            email: p.email ?? '',
            cellNumber: p.phone ?? null,
            altContact: null,
            telephone: null,
            isMember: p.isMember,
            isActive: true,
            avatar: p.avatar ?? null,
            typeID: 0,
            memberCode: null,
            modifiedDateTime: 0,
            createdDateTime: null,
            isMain: link.isMain,
            isApplicant: link.isApplicant,
            hasConsent: link.hasConsent,
          };
        }

        // ⚠️ Linked but not in the view's participants — the case that used to vanish silently.
        // One lookup for this person only, so the common path stays at two requests.
        try {
          const res: any = await memberService.searchMembers(String(link.memberID));
          const list: any[] = res?.data ?? res ?? [];
          const profile = list.find((m) => m.memberID === link.memberID) ?? list[0];
          if (profile) {
            return {
              ...profile,
              isMain: link.isMain,
              isApplicant: link.isApplicant,
              hasConsent: link.hasConsent,
            };
          }
        } catch { /* fall through to the bare row */ }

        return {
          memberID: link.memberID,
          userId: '',
          firstName: 'Member',
          middleName: null,
          lastName: String(link.memberID),
          dateOfBirth: null,
          gender: null,
          maritalStatusID: 0,
          email: '',
          cellNumber: null,
          altContact: null,
          telephone: null,
          isMember: true,
          isActive: true,
          avatar: null,
          typeID: 0,
          memberCode: null,
          modifiedDateTime: 0,
          createdDateTime: null,
          isMain: link.isMain,
          isApplicant: link.isApplicant,
          hasConsent: link.hasConsent,
        };
      }));

      setMembers(enriched as any);
    } catch (e) {
      console.error('Error fetching registration members:', e);
      toast.error('Error', 'Could not load participants.');
    } finally {
      setLoadingMembers(false);
    }
  }, [registrationId, registrationService, memberService]);

  useEffect(() => { if (active) fetchMembers(); }, [active, fetchMembers]);

  useEffect(() => {
    if (!active || intakeID == null) return;
    let cancelled = false;
    intakeService.getById(intakeID)
      .then((intake) => {
        if (cancelled) return;
        const map: Record<number, {
          dateOfBirth?: string | null; gender?: string | null;
          email?: string | null; phone?: string | null;
        }> = {};
        (intake?.participants ?? []).forEach((p) => {
          if (p.memberID != null) {
            map[p.memberID] = {
              dateOfBirth: p.dateOfBirth, gender: p.gender, email: p.email, phone: p.phone,
            };
          }
        });
        // The applicant is on the intake's top-level fields rather than in participants.
        if (intake?.matchedMemberID != null) {
          map[intake.matchedMemberID] = {
            dateOfBirth: null, gender: intake.gender, email: intake.email, phone: intake.phone,
          };
        }
        setSubmittedByMemberID(map);
      })
      .catch((e) => console.error('[MembersTab] could not load the intake for merge details:', e));
    return () => { cancelled = true; };
  }, [active, intakeID, intakeService]);

  /**
   * The sign-up values for a member, but only when at least one would actually change something.
   *
   * ⚠️ Returning null hides the action entirely. An icon that opens onto "nothing to apply" is worse
   * than no icon, and it would appear on every member of every imported registration.
   */
  const submittedFor = useCallback((m: Member) => {
    const sub = submittedByMemberID[m.memberID];
    if (!sub) return null;
    const differs = (cur?: string | null, next?: string | null) => {
      const n = (next ?? '').trim();
      return n !== '' && n !== (cur ?? '').trim();
    };
    const worth = differs(m.dateOfBirth, sub.dateOfBirth)
      || differs(m.gender, sub.gender)
      || differs(m.email, sub.email)
      || differs(m.cellNumber, sub.phone);
    return worth ? sub : null;
  }, [submittedByMemberID]);

  const handleAdded = async () => {
    await fetchMembers();
    onRegistrationChange?.();
  };

  const handleRemoveMember = async (member: Member) => {
    setRemovingId(member.memberID);
    try {
      await registrationService.removeMember(registrationId, member.memberID);
      await fetchMembers();
      onRegistrationChange?.();
      toast.success('Removed', `${fullName(member)} removed from the registration.`);
    } catch (e) {
      console.error('Error removing member:', e);
      toast.error('Error', 'Could not remove member. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const alreadyAdded = (m: Member) =>
    regMemberIDs.includes(m.memberID);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm" c="dimmed">
            Members on this registration ({members.length})
          </Text>

          <Group gap={6}>
            <Tooltip label="Refresh members" withArrow>
              <ActionIcon
                variant="subtle"
                onClick={fetchMembers}
                loading={loadingMembers}
                size="lg"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
              <Button
                size="sm"
                variant="light"
                leftSection={<IconFileImport size={14} />}
                onClick={() => setImportOpen(true)}
              >
                Import from intake
              </Button>
            <Button
              size="sm"
              leftSection={<IconUserPlus size={14} />}
              onClick={() => setDrawerOpened(true)}
            >
              Add Member
            </Button>
          </Group>
        </Group>

        {loadingMembers ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <MemberCardSkeleton />
            <MemberCardSkeleton />
            <MemberCardSkeleton />
          </SimpleGrid>
        ) : members.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text fw={600}>No members yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                Click "Add Member" to search for someone to add to this registration.
              </Text>
              <Button
                size="sm"
                variant="light"
                leftSection={<IconUserPlus size={14} />}
                onClick={() => setDrawerOpened(true)}
              >
                Add Member
              </Button>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {members.map((c) => (
              <MemberCard
                key={c.memberID}
                member={c}
                removing={removingId === c.memberID}
                onRemove={() => handleRemoveMember(c)}
                onOpenDetails={() => setDetailMember(c)}
                onFillFromIntake={submittedFor(c) ? () => setMergeMember(c) : undefined}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <AddMembersDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        registrationId={registrationId}
        alreadyAdded={alreadyAdded}
        onAdded={handleAdded}
      />

        <ImportFromIntakeModal
          registrationID={registrationId}
          intakeID={intakeID}
          existingMemberIDs={members.map((m) => m.memberID)}
          opened={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={handleAdded}
        />

      {mergeMember && submittedFor(mergeMember) && (
        <MergeFromSubmissionModal
          memberID={mergeMember.memberID}
          memberName={fullName(mergeMember)}
          current={{
            dateOfBirth: mergeMember.dateOfBirth,
            gender: mergeMember.gender,
            email: mergeMember.email,
            cellNumber: mergeMember.cellNumber,
          }}
          submitted={submittedFor(mergeMember)!}
          sourceLabel="sign-up"
          opened={mergeMember !== null}
          onClose={() => setMergeMember(null)}
          onMerged={handleAdded}
        />
      )}

      {/* The card only has room for a name and one contact line; the rest lives here. */}
      <MemberDetailModal
        member={detailMember}
        opened={detailMember !== null}
        onClose={() => setDetailMember(null)}
        contextLabel="registration"
      />
    </Stack>
  );
}